import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  MessageCircle, 
  Save, 
  RotateCcw,
  Calendar,
  Clock,
  Key,
  Home,
  Star,
  Info,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DEFAULT_TEMPLATES = {
  booking_confirmation: {
    name: "Conferma Prenotazione",
    icon: "📅",
    description: "Inviato subito dopo la prenotazione",
    message: `🏠 *La Maisonette di Paestum*

Ciao {{guest_name}}! 👋

Grazie per aver prenotato con noi!

📅 *Dettagli soggiorno:*
• Check-in: {{check_in_date}}
• Check-out: {{check_out_date}}
• Ospiti: {{num_guests}}
• Codice: {{booking_code}}

💰 *Totale:* €{{total_price}}

📍 *Indirizzo:*
Via Magna Grecia 145, Capaccio Paestum (SA)

Ti invieremo le istruzioni per il check-in qualche giorno prima del tuo arrivo.

Per qualsiasi domanda, rispondi a questo messaggio!

A presto! 🌟`
  },
  pre_arrival_reminder: {
    name: "Reminder Pre-Arrivo",
    icon: "⏰",
    description: "Inviato 2-3 giorni prima dell'arrivo",
    message: `🏠 *La Maisonette di Paestum*

Ciao {{guest_name}}! 👋

Il tuo soggiorno si avvicina! 🎉

📅 *Check-in:* {{check_in_date}} dalle 15:00
📅 *Check-out:* {{check_out_date}} entro le 10:00

📍 *Come raggiungerci:*
Via Magna Grecia 145, Capaccio Paestum (SA)
📍 Google Maps: https://maps.google.com/?q=La+Maisonette+Paestum

🚗 *Parcheggio:* Gratuito all'interno della struttura

Ti invieremo il codice d'ingresso il giorno del check-in.

Hai domande? Rispondi qui! 💬`
  },
  checkin_instructions: {
    name: "Istruzioni Check-in",
    icon: "🔑",
    description: "Inviato il giorno del check-in con codice ingresso",
    message: `🏠 *La Maisonette di Paestum*

Ciao {{guest_name}}! 👋

Oggi è il grande giorno! 🎉

🔑 *CODICE INGRESSO:* {{entry_code}}

⏰ *Check-in:* dalle 15:00 alle 22:00

📍 *Indirizzo:*
Via Magna Grecia 145, Capaccio Paestum (SA)

📋 *Istruzioni:*
1. Arriva all'ingresso principale
2. Digita il codice {{entry_code}} sulla tastiera
3. La porta si aprirà automaticamente
4. La tua camera è la *{{room_name}}*

📶 *WiFi:*
• Rete: {{wifi_network}}
• Password: {{wifi_password}}

Per qualsiasi problema, chiamaci: {{phone_number}}

Buon soggiorno! 🌟`
  },
  welcome_arrival: {
    name: "Benvenuto in Struttura",
    icon: "🏡",
    description: "Inviato quando l'ospite arriva",
    message: `🏠 *La Maisonette di Paestum*

Bentornato {{guest_name}}! 🎉

Speriamo che il viaggio sia andato bene!

🛎️ *Servizi disponibili:*
• Colazione: 8:00 - 10:00
• Piscina: 9:00 - 19:00
• Reception virtuale: 24/7

📍 *Cosa fare nei dintorni:*
• Templi di Paestum (5 min)
• Spiaggia (10 min)
• Centro storico (15 min)

🍽️ *Ristoranti consigliati:*
• Nettuno (pesce fresco)
• Il Granaio (cucina tipica)

Se hai bisogno di qualcosa, scrivi qui!

Goditi il soggiorno! 🌟`
  },
  checkout_review: {
    name: "Richiesta Recensione",
    icon: "⭐",
    description: "Inviato dopo il check-out",
    message: `🏠 *La Maisonette di Paestum*

Ciao {{guest_name}}! 👋

Grazie per aver soggiornato con noi! 🙏

Speriamo che tu abbia trascorso un soggiorno indimenticabile.

⭐ *Ti è piaciuto?*
La tua opinione è preziosa! Se hai un minuto, lasciaci una recensione:

📝 *Google:* {{google_review_link}}

🎁 *Sconto 10%* sul prossimo soggiorno se prenoti direttamente!

Speriamo di rivederti presto! 
A presto! 💛`
  }
};

const AVAILABLE_VARIABLES = [
  { key: 'guest_name', description: 'Nome ospite' },
  { key: 'check_in_date', description: 'Data check-in' },
  { key: 'check_out_date', description: 'Data check-out' },
  { key: 'num_guests', description: 'Numero ospiti' },
  { key: 'booking_code', description: 'Codice prenotazione' },
  { key: 'total_price', description: 'Prezzo totale' },
  { key: 'entry_code', description: 'Codice ingresso' },
  { key: 'room_name', description: 'Nome camera' },
  { key: 'wifi_network', description: 'Nome rete WiFi' },
  { key: 'wifi_password', description: 'Password WiFi' },
  { key: 'phone_number', description: 'Numero telefono struttura' },
  { key: 'google_review_link', description: 'Link recensione Google' },
];

export default function AdminWhatsAppTemplates() {
  const { token } = useAuth();
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedTemplate, setExpandedTemplate] = useState(null);
  const [showVariables, setShowVariables] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/admin/whatsapp/templates-editable`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Merge with defaults for any missing templates
      const merged = { ...DEFAULT_TEMPLATES };
      if (response.data.templates) {
        Object.keys(response.data.templates).forEach(key => {
          merged[key] = { ...DEFAULT_TEMPLATES[key], ...response.data.templates[key] };
        });
      }
      setTemplates(merged);
    } catch (error) {
      // Use defaults if no saved templates
      setTemplates(DEFAULT_TEMPLATES);
    } finally {
      setLoading(false);
    }
  };

  const saveTemplate = async (templateKey) => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/whatsapp/templates-editable`, {
        template_key: templateKey,
        template: templates[templateKey]
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template salvato!');
    } catch (error) {
      toast.error('Errore salvataggio template');
    } finally {
      setSaving(false);
    }
  };

  const resetTemplate = (templateKey) => {
    setTemplates(prev => ({
      ...prev,
      [templateKey]: DEFAULT_TEMPLATES[templateKey]
    }));
    toast.info('Template ripristinato ai valori predefiniti');
  };

  const updateTemplate = (templateKey, field, value) => {
    setTemplates(prev => ({
      ...prev,
      [templateKey]: {
        ...prev[templateKey],
        [field]: value
      }
    }));
  };

  const insertVariable = (templateKey, variable) => {
    const textarea = document.getElementById(`textarea-${templateKey}`);
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = templates[templateKey].message;
      const newText = text.substring(0, start) + `{{${variable}}}` + text.substring(end);
      updateTemplate(templateKey, 'message', newText);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Template WhatsApp">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Template WhatsApp">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#1A202C] flex items-center gap-3">
              <MessageCircle className="w-7 h-7 text-green-600" />
              Template Messaggi WhatsApp
            </h1>
            <p className="text-gray-500 mt-1">Personalizza i messaggi automatici per gli ospiti</p>
          </div>
        </div>

        {/* Variables Reference */}
        <Card className="border-blue-200 bg-blue-50">
          <CardHeader className="pb-2 cursor-pointer" onClick={() => setShowVariables(!showVariables)}>
            <CardTitle className="text-sm font-medium flex items-center justify-between text-blue-800">
              <div className="flex items-center gap-2">
                <Info className="w-4 h-4" />
                Variabili disponibili
              </div>
              {showVariables ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </CardTitle>
          </CardHeader>
          {showVariables && (
            <CardContent className="pt-0">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {AVAILABLE_VARIABLES.map(v => (
                  <div key={v.key} className="text-xs bg-white rounded px-2 py-1 border border-blue-200">
                    <code className="text-blue-600">{`{{${v.key}}}`}</code>
                    <span className="text-gray-500 ml-1">- {v.description}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>

        {/* Templates */}
        <div className="space-y-4">
          {Object.entries(templates).map(([key, template]) => (
            <Card key={key} className="border-[#E2E8F0]">
              <CardHeader 
                className="cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setExpandedTemplate(expandedTemplate === key ? null : key)}
              >
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{template.icon}</span>
                    <div>
                      <h3 className="font-semibold text-[#1A202C]">{template.name}</h3>
                      <p className="text-sm text-gray-500 font-normal">{template.description}</p>
                    </div>
                  </div>
                  {expandedTemplate === key ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </CardTitle>
              </CardHeader>
              
              {expandedTemplate === key && (
                <CardContent className="border-t">
                  <div className="space-y-4">
                    {/* Template Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nome Template
                      </label>
                      <Input
                        value={template.name}
                        onChange={(e) => updateTemplate(key, 'name', e.target.value)}
                        className="max-w-md"
                      />
                    </div>

                    {/* Message */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Messaggio
                      </label>
                      <div className="mb-2 flex flex-wrap gap-1">
                        {AVAILABLE_VARIABLES.slice(0, 6).map(v => (
                          <Button
                            key={v.key}
                            type="button"
                            variant="outline"
                            size="sm"
                            className="text-xs h-7"
                            onClick={() => insertVariable(key, v.key)}
                          >
                            {`{{${v.key}}}`}
                          </Button>
                        ))}
                      </div>
                      <Textarea
                        id={`textarea-${key}`}
                        value={template.message}
                        onChange={(e) => updateTemplate(key, 'message', e.target.value)}
                        rows={12}
                        className="font-mono text-sm"
                      />
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3 pt-2">
                      <Button
                        onClick={() => saveTemplate(key)}
                        disabled={saving}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        {saving ? 'Salvataggio...' : 'Salva Template'}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => resetTemplate(key)}
                      >
                        <RotateCcw className="w-4 h-4 mr-2" />
                        Ripristina Default
                      </Button>
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}
